import { Box, Card, Stack, Typography, Skeleton } from '@mui/material';

import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { PortableTextViewer } from '@/components/organisms/RichTextEditor/PortableTextViewer';
import type { PortableTextContent } from '@/components/organisms/RichTextEditor/portableText';

export interface CallSummaryPageProps {
  title: string;
  when?: string;
  withName?: string;
  content?: PortableTextContent;
  isLoading?: boolean;
  hasError?: boolean;
  onBack?: () => void;
}

export const CallSummaryPage = ({
  title,
  when,
  withName,
  content,
  isLoading = false,
  hasError = false,
  onBack,
}: CallSummaryPageProps) => {
  const description = [when, withName && `with ${withName}`]
    .filter(Boolean)
    .join(' · ');

  const hasContent = content && content.length > 0;

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='md'
        headerSlot={
          isLoading ? (
            <Box>
              <Skeleton variant='text' width='60%' height={40} />
              <Skeleton variant='text' width='40%' height={24} />
            </Box>
          ) : (
            <PageHeader
              variant='h2'
              title={title}
              description={description || undefined}
            />
          )
        }
        contentSlot={
          <Stack spacing={2}>
            <Box>
              <Typography
                variant='overline'
                sx={{
                  color: 'text.secondary',
                }}
              >
                CALL SUMMARY
              </Typography>
            </Box>
            {isLoading ? (
              <Card>
                <Box
                  sx={{
                    p: 2,
                  }}
                >
                  <Stack spacing={1}>
                    <Skeleton variant='text' width='90%' />
                    <Skeleton variant='text' width='75%' />
                    <Skeleton variant='text' width='85%' />
                    <Skeleton variant='text' width='60%' />
                  </Stack>
                </Box>
              </Card>
            ) : hasError ? (
              <Box
                sx={{
                  bgcolor: 'error.light',
                  borderRadius: 3,
                  p: 2,
                }}
              >
                <Typography
                  variant='body1'
                  sx={{
                    color: 'error.dark',
                  }}
                >
                  Unable to load call summary. Please try again later.
                </Typography>
              </Box>
            ) : hasContent ? (
              <Card>
                <Box
                  sx={{
                    p: 2,
                  }}
                >
                  <PortableTextViewer content={content} />
                </Box>
              </Card>
            ) : (
              <Box
                sx={{
                  bgcolor: 'custom.cherry.100',
                  borderRadius: 3,
                  p: 2,
                }}
              >
                <Typography variant='body1'>
                  Your call summary will appear here. You will be notified when
                  it is ready.
                </Typography>
              </Box>
            )}
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

CallSummaryPage.displayName = 'CallSummaryPage';
export default CallSummaryPage;
